import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Reusable inquiry form -> POST /api/leads
export default function LeadForm({
  leadType = "contact",
  categoryOptions = null,
  defaultCategory = "",
  showCompany = true,
  showPhone = true,
  messageLabel = "Message",
  submitLabel = "SUBMIT INQUIRY",
  testId = "lead-form",
  compact = false,
}) {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", category: defaultCategory, message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, lead_type: leadType, source: window.location.pathname });
      setDone(true);
      toast.success("Inquiry received. We'll be in touch.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testId}-success`} className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
        <h3 className="font-display text-lg font-semibold text-foreground">Thank you</h3>
        <p className="text-sm text-muted-foreground mt-1">Your inquiry has been received. A member of our team will follow up.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={testId} className="space-y-4">
      <div className={`grid gap-4 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        <div>
          <Label className="text-xs text-muted-foreground">Name *</Label>
          <Input required value={form.name} onChange={set("name")} data-testid={`${testId}-name`} className="mt-1.5 bg-secondary/50 border-border" />
        </div>
        {showCompany && (
          <div>
            <Label className="text-xs text-muted-foreground">Company</Label>
            <Input value={form.company} onChange={set("company")} data-testid={`${testId}-company`} className="mt-1.5 bg-secondary/50 border-border" />
          </div>
        )}
        <div>
          <Label className="text-xs text-muted-foreground">Email *</Label>
          <Input required type="email" value={form.email} onChange={set("email")} data-testid={`${testId}-email`} className="mt-1.5 bg-secondary/50 border-border" />
        </div>
        {showPhone && (
          <div>
            <Label className="text-xs text-muted-foreground">Phone</Label>
            <Input value={form.phone} onChange={set("phone")} data-testid={`${testId}-phone`} className="mt-1.5 bg-secondary/50 border-border" />
          </div>
        )}
      </div>

      {categoryOptions && (
        <div>
          <Label className="text-xs text-muted-foreground">Inquiry Type</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid={`${testId}-category`} className="mt-1.5 bg-secondary/50 border-border">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label className="text-xs text-muted-foreground">{messageLabel}</Label>
        <Textarea value={form.message} onChange={set("message")} rows={4} data-testid={`${testId}-message`} className="mt-1.5 bg-secondary/50 border-border" />
      </div>

      <Button type="submit" disabled={loading} data-testid={`${testId}-submit`} className="w-full bg-primary hover:bg-primary/90 text-white font-semibold rounded-full h-11">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
      </Button>
    </form>
  );
}
